/**
 * Client for the app-level 2FA endpoints (`/auth/mfa/*`). Passkey ceremonies
 * use @simplewebauthn/browser; TOTP + email are plain code entry.
 */
import { apiRequest } from './api';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

export type MfaMethod = 'totp' | 'email' | 'passkey';
/** Recovery codes satisfy a challenge but aren't an enrollable factor. */
export type ChallengeMethod = MfaMethod | 'recovery';

export interface MfaFactor { id: string; type: MfaMethod; label: string; createdAt: number | null; lastUsedAt: number | null; }
export interface MfaStatus {
  enabled: boolean;
  methods: MfaMethod[];
  factors: MfaFactor[];
  orgPolicy: { mode: 'off' | 'allowed' | 'required'; required: boolean; graceEndsAtMs: number | null };
  recoveryCodes: { remaining: number };
}
export interface TrustedDevice { hash: string; createdAt: number | null; expiresAt: number | null; userAgent: string | null; }
export interface MfaSessionResult {
  challenge: boolean;
  satisfied?: boolean;
  enrolled: boolean;
  mfaToken?: string;
  expiresAtMs?: number;
  enrollmentRequired?: boolean;
  methods?: MfaMethod[];
  recoveryAvailable?: boolean;
  orgMode: 'off' | 'allowed' | 'required';
  graceEndsAtMs?: number | null;
  blocking?: boolean;
}

const DEVICE_KEY = 'simcheck.mfa.deviceId';
export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

const post = <T,>(endpoint: string, body: unknown, orgId?: string) =>
  apiRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(body ?? {}) }, orgId);

export const getMfaStatus = (orgId?: string) =>
  apiRequest<MfaStatus>('/auth/mfa/status', { method: 'GET' }, orgId);

export const mfaSession = (orgId?: string) =>
  post<MfaSessionResult>('/auth/mfa/session', { deviceId: getDeviceId() }, orgId);

// --- enrollment ---
export interface EnrollResult {
  ok: boolean;
  methods: MfaMethod[];
  mfaToken?: string;
  expiresAtMs?: number;
  sudoToken: string;
  sudoExpiresAtMs: number;
  /** Present only on the FIRST factor — plaintext, shown once, never re-readable. */
  recoveryCodes?: string[];
}

export const enrollTotpStart = (orgId?: string) =>
  post<{ otpauth: string; qrDataUrl: string; manualKey: string }>('/auth/mfa/enroll/totp/start', {}, orgId);
export const enrollTotpVerify = (code: string, label: string, orgId?: string) =>
  post<EnrollResult>('/auth/mfa/enroll/totp/verify', { code, label, deviceId: getDeviceId() }, orgId);

export const enrollEmailStart = (orgId?: string) =>
  post<{ ok: boolean; throttled: boolean; sentTo: string }>('/auth/mfa/enroll/email/start', {}, orgId);
export const enrollEmailVerify = (code: string, orgId?: string) =>
  post<EnrollResult>('/auth/mfa/enroll/email/verify', { code, deviceId: getDeviceId() }, orgId);

export async function enrollPasskey(label: string, orgId?: string) {
  const { options } = await post<{ options: any }>('/auth/mfa/enroll/passkey/start', {}, orgId);
  const attestation = await startRegistration(options);
  return post<EnrollResult>('/auth/mfa/enroll/passkey/verify', { attestation, label, deviceId: getDeviceId() }, orgId);
}

// --- challenge / verify ---
export const challengeStart = (method: ChallengeMethod, orgId?: string) =>
  post<{ ok: boolean; throttled?: boolean; options?: any }>('/auth/mfa/challenge/start', { method }, orgId);

export interface MfaVerifyResult {
  ok: boolean;
  mfaToken: string;
  expiresAtMs: number;
  sudoToken: string;
  sudoExpiresAtMs: number;
  /** Only when a recovery code was redeemed — warn the user as they run low. */
  recoveryCodesRemaining?: number;
}
export const verifyMfa = (
  args: { method: ChallengeMethod; code?: string; assertion?: any; rememberDevice: boolean },
  orgId?: string,
) => post<MfaVerifyResult>('/auth/mfa/verify', { ...args, deviceId: getDeviceId() }, orgId);

export async function passkeyChallenge(orgId?: string) {
  const { options } = await challengeStart('passkey', orgId);
  return startAuthentication(options);
}

// --- management ---
export const removeFactor = (id: string, orgId?: string) =>
  apiRequest<{ ok: boolean; methods: MfaMethod[] }>(`/auth/mfa/factors/${id}`, { method: 'DELETE' }, orgId);

/** Returns plaintext codes ONCE — they're hashed server-side, so if the user
 *  navigates away without saving them, the only path forward is regenerating. */
export const generateRecoveryCodes = (orgId?: string) =>
  post<{ ok: boolean; codes: string[] }>('/auth/mfa/recovery-codes', {}, orgId);

export const listTrustedDevices = (orgId?: string) =>
  apiRequest<{ devices: TrustedDevice[] }>('/auth/mfa/devices', { method: 'GET' }, orgId);

export const revokeTrustedDevice = (hash: string, orgId?: string) =>
  apiRequest<{ ok: boolean }>(`/auth/mfa/devices/${hash}`, { method: 'DELETE' }, orgId);

// --- admin recovery ---
export const adminResetMfa = (uid: string) =>
  post<{ ok: boolean }>(`/admin/users/${uid}/mfa/reset`, {});
export const orgResetMemberMfa = (orgId: string, uid: string) =>
  post<{ ok: boolean }>(`/organizations/${orgId}/members/${uid}/mfa/reset`, {}, orgId);
